import type { TranslationMessages } from "ra-core";

/**
 * German translation of the react-admin core messages.
 *
 * There is no usable `ra-language-german` package for this react-admin version
 * (the published one is 3.13.5), so the catalog is maintained here. It mirrors
 * the key set of `ra-language-english` 5.x.
 */
export const germanRaMessages = {
  ra: {
    action: {
      add_filter: "Filter hinzufügen",
      add: "Hinzufügen",
      back: "Zurück",
      bulk_actions:
        "1 Element ausgewählt |||| %{smart_count} Elemente ausgewählt",
      cancel: "Abbrechen",
      clear_array_input: "Liste leeren",
      clear_input_value: "Eingabe löschen",
      clone: "Duplizieren",
      confirm: "Bestätigen",
      create: "Erstellen",
      create_item: "%{item} erstellen",
      delete: "Löschen",
      edit: "Bearbeiten",
      export: "Exportieren",
      list: "Liste",
      refresh: "Aktualisieren",
      remove_filter: "Diesen Filter entfernen",
      remove_all_filters: "Alle Filter entfernen",
      remove: "Entfernen",
      reset: "Zurücksetzen",
      save: "Speichern",
      search: "Suchen",
      search_columns: "Spalten durchsuchen",
      select_all: "Alle auswählen",
      select_all_button: "Alle auswählen",
      select_row: "Diese Zeile auswählen",
      show: "Anzeigen",
      sort: "Sortieren",
      undo: "Rückgängig",
      unselect: "Auswahl aufheben",
      expand: "Aufklappen",
      close: "Schließen",
      open_menu: "Menü öffnen",
      close_menu: "Menü schließen",
      update: "Ändern",
      move_up: "Nach oben verschieben",
      move_down: "Nach unten verschieben",
      open: "Öffnen",
      toggle_theme: "Hell-/Dunkelmodus umschalten",
      select_columns: "Spalten",
      update_application: "Anwendung neu laden",
    },
    boolean: {
      true: "Ja",
      false: "Nein",
      null: " ",
    },
    page: {
      create: "%{name} erstellen",
      dashboard: "Dashboard",
      edit: "%{name} %{recordRepresentation}",
      error: "Es ist ein Fehler aufgetreten",
      list: "%{name}",
      loading: "Wird geladen",
      not_found: "Nicht gefunden",
      show: "%{name} %{recordRepresentation}",
      empty: "Noch keine %{name} vorhanden.",
      invite: "Möchten Sie einen Eintrag anlegen?",
      access_denied: "Zugriff verweigert",
      authentication_error: "Authentifizierungsfehler",
    },
    input: {
      file: {
        upload_several:
          "Dateien zum Hochladen hier ablegen oder klicken, um sie auszuwählen.",
        upload_single:
          "Datei zum Hochladen hier ablegen oder klicken, um sie auszuwählen.",
      },
      image: {
        upload_several:
          "Bilder zum Hochladen hier ablegen oder klicken, um sie auszuwählen.",
        upload_single:
          "Bild zum Hochladen hier ablegen oder klicken, um es auszuwählen.",
      },
      references: {
        all_missing:
          "Die zugehörigen Datensätze konnten nicht gefunden werden.",
        many_missing:
          "Mindestens einer der zugehörigen Datensätze ist offenbar nicht mehr verfügbar.",
        single_missing:
          "Der zugehörige Datensatz ist offenbar nicht mehr verfügbar.",
      },
      password: {
        toggle_visible: "Passwort verbergen",
        toggle_hidden: "Passwort anzeigen",
      },
    },
    message: {
      about: "Über",
      access_denied:
        "Sie haben nicht die erforderlichen Berechtigungen für diese Seite",
      are_you_sure: "Sind Sie sicher?",
      authentication_error:
        "Der Authentifizierungsserver hat einen Fehler zurückgegeben, Ihre Zugangsdaten konnten nicht geprüft werden.",
      auth_error:
        "Bei der Prüfung des Authentifizierungs-Tokens ist ein Fehler aufgetreten.",
      bulk_delete_content:
        "Möchten Sie %{name} wirklich löschen? |||| Möchten Sie diese %{smart_count} Elemente wirklich löschen?",
      bulk_delete_title: "%{name} löschen |||| %{smart_count} %{name} löschen",
      bulk_update_content:
        "Möchten Sie %{name} %{recordRepresentation} wirklich ändern? |||| Möchten Sie diese %{smart_count} Elemente wirklich ändern?",
      bulk_update_title:
        "%{name} %{recordRepresentation} ändern |||| %{smart_count} %{name} ändern",
      clear_array_input: "Möchten Sie die gesamte Liste wirklich leeren?",
      delete_content: "Möchten Sie %{name} wirklich löschen?",
      delete_title: "%{name} %{recordRepresentation} löschen",
      details: "Details",
      error:
        "Es ist ein Fehler im Browser aufgetreten, Ihre Anfrage konnte nicht abgeschlossen werden.",
      invalid_form:
        "Das Formular ist nicht gültig. Bitte prüfen Sie die markierten Felder",
      loading: "Bitte warten",
      no: "Nein",
      not_found:
        "Entweder haben Sie eine falsche URL eingegeben oder einem fehlerhaften Link gefolgt.",
      select_all_limit_reached:
        "Es sind zu viele Elemente vorhanden, um alle auszuwählen. Nur die ersten %{max} Elemente wurden ausgewählt.",
      unsaved_changes:
        "Einige Ihrer Änderungen wurden nicht gespeichert. Möchten Sie sie wirklich verwerfen?",
      yes: "Ja",
      placeholder_data_warning:
        "Netzwerkproblem: Die Daten konnten nicht aktualisiert werden.",
    },
    navigation: {
      clear_filters: "Filter zurücksetzen",
      no_filtered_results: "Keine %{name} mit den aktuellen Filtern gefunden.",
      no_results: "Keine %{name} gefunden",
      no_more_results:
        "Die Seite %{page} liegt außerhalb des gültigen Bereichs. Versuchen Sie die vorherige Seite.",
      page_out_of_boundaries:
        "Seite %{page} liegt außerhalb des gültigen Bereichs",
      page_out_from_end: "Nach der letzten Seite geht es nicht weiter",
      page_out_from_begin: "Vor Seite 1 geht es nicht zurück",
      page_range_info: "%{offsetBegin}-%{offsetEnd} von %{total}",
      partial_page_range_info:
        "%{offsetBegin}-%{offsetEnd} von mehr als %{offsetEnd}",
      current_page: "Seite %{page}",
      page: "Zu Seite %{page}",
      first: "Zur ersten Seite",
      last: "Zur letzten Seite",
      next: "Zur nächsten Seite",
      previous: "Zur vorherigen Seite",
      page_rows_per_page: "Zeilen pro Seite:",
      skip_nav: "Zum Inhalt springen",
    },
    sort: {
      sort_by: "Nach %{field} %{order} sortieren",
      ASC: "aufsteigend",
      DESC: "absteigend",
    },
    auth: {
      auth_check_error: "Bitte melden Sie sich an, um fortzufahren",
      user_menu: "Profil",
      username: "Benutzername",
      password: "Passwort",
      email: "E-Mail-Adresse",
      sign_in: "Anmelden",
      sign_in_error: "Anmeldung fehlgeschlagen, bitte erneut versuchen",
      logout: "Abmelden",
    },
    notification: {
      updated: "Element aktualisiert |||| %{smart_count} Elemente aktualisiert",
      created: "Element erstellt",
      deleted: "Element gelöscht |||| %{smart_count} Elemente gelöscht",
      bad_item: "Fehlerhaftes Element",
      item_doesnt_exist: "Das Element existiert nicht",
      http_error: "Fehler bei der Kommunikation mit dem Server",
      data_provider_error:
        "Fehler im dataProvider. Details finden Sie in der Konsole.",
      i18n_error:
        "Die Übersetzungen für die gewählte Sprache konnten nicht geladen werden",
      canceled: "Aktion abgebrochen",
      logged_out:
        "Ihre Sitzung ist abgelaufen, bitte melden Sie sich erneut an.",
      not_authorized:
        "Sie sind nicht berechtigt, auf diese Ressource zuzugreifen.",
      application_update_available: "Eine neue Version ist verfügbar.",
      offline: "Keine Verbindung. Die Daten konnten nicht geladen werden.",
    },
    validation: {
      required: "Erforderlich",
      minLength: "Muss mindestens %{min} Zeichen lang sein",
      maxLength: "Darf höchstens %{max} Zeichen lang sein",
      minValue: "Muss mindestens %{min} sein",
      maxValue: "Darf höchstens %{max} sein",
      number: "Muss eine Zahl sein",
      email: "Muss eine gültige E-Mail-Adresse sein",
      oneOf: "Muss einer der folgenden Werte sein: %{options}",
      regex: "Muss einem bestimmten Format entsprechen (Regex): %{pattern}",
      unique: "Muss eindeutig sein",
    },
    saved_queries: {
      label: "Gespeicherte Abfragen",
      query_name: "Name der Abfrage",
      new_label: "Aktuelle Abfrage speichern...",
      new_dialog_title: "Aktuelle Abfrage speichern als",
      remove_label: "Gespeicherte Abfrage entfernen",
      remove_label_with_name: 'Abfrage "%{name}" entfernen',
      remove_dialog_title: "Gespeicherte Abfrage entfernen?",
      remove_message:
        "Möchten Sie diesen Eintrag wirklich aus Ihren gespeicherten Abfragen entfernen?",
      help: "Filtern Sie die Liste und speichern Sie diese Abfrage für später",
    },
    guesser: {
      empty: {
        title: "Keine Daten vorhanden",
        message: "Bitte prüfen Sie Ihren Data Provider",
      },
    },
    configurable: {
      customize: "Anpassen",
      configureMode: "Diese Seite konfigurieren",
      inspector: {
        title: "Inspektor",
        content:
          "Fahren Sie mit der Maus über die Oberflächenelemente, um sie zu konfigurieren",
        reset: "Einstellungen zurücksetzen",
        hideAll: "Alle ausblenden",
        showAll: "Alle anzeigen",
      },
      Datagrid: {
        title: "Tabelle",
        unlabeled: "Spalte ohne Bezeichnung #%{column}",
      },
      SimpleForm: {
        title: "Formular",
        unlabeled: "Feld ohne Bezeichnung #%{input}",
      },
      SimpleList: {
        title: "Liste",
        primaryText: "Haupttext",
        secondaryText: "Sekundärtext",
        tertiaryText: "Zusatztext",
      },
    },
  },
} satisfies TranslationMessages;
